import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, Link } from 'react-router-dom';
import {
  DollarSign, Activity, ArrowDownToLine, ArrowUpFromLine,
  Users as UsersIcon, FolderKanban, Layers, RefreshCw, TrendingUp,
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { getLLMStats, getTopLLMUsers, getTiers, getProjects, getUsers } from '../api';
import { isOrgManager } from '../org';
import UserUsageModal from './UserUsageModal';

const RANGES = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: 'All', days: null },
];

const usd = (n) => '$' + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// "All time" collapses to month buckets ('YYYY-MM'); 7d/30d/90d stay daily.
// Hardcoded, not toLocaleDateString — Intl's 'short' month for September is
// locale-dependent ("Sep" vs "Sept"), inconsistent next to the other months.
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatDateTick = (d) => {
  if (typeof d === 'string' && /^\d{4}-\d{2}$/.test(d)) {
    const [y, m] = d.split('-');
    return `${MONTH_ABBR[Number(m) - 1]} ${y.slice(2)}`;
  }
  return d;
};
// Explicit tick spacing instead of Recharts' width-based default, which skips
// a different number of labels per chart size. The +2 slack avoids jumping
// from "show all" straight to "every 2nd" when count is barely over target.
const tickInterval = (n, target = 7) => (n <= target + 2 ? 0 : Math.max(0, Math.ceil(n / target) - 1));
const compact = (n) => {
  n = Number(n) || 0;
  if (n >= 1e9) return (n / 1e9).toFixed(1) + 'B';
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return String(n);
};

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-2xl border bg-panel p-5 ${accent ? 'border-orange/40' : 'border-panel-border'}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-mono uppercase tracking-widest text-fg-muted">{label}</span>
        <span className={`inline-flex items-center justify-center h-8 w-8 rounded-lg ${accent ? 'bg-orange/15 text-orange' : 'bg-fg/5 text-fg-muted'}`}>
          <Icon className="w-4 h-4" />
        </span>
      </div>
      <div className="mt-3 font-display text-2xl font-bold tabular-nums text-fg">{value}</div>
    </div>
  );
}

function Panel({ title, action, children, className = '' }) {
  return (
    <div className={`rounded-2xl border border-panel-border bg-panel p-5 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-sm font-semibold text-fg">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

const tooltipStyle = {
  contentStyle: { background: 'var(--panel, #15171c)', border: '1px solid rgba(128,128,128,0.25)', borderRadius: 10, fontSize: 12 },
  labelStyle: { color: '#9aa0a6' },
};

export default function Dashboard() {
  const isAdmin = localStorage.getItem('admin_is_admin') === 'true' || isOrgManager();
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [topUsers, setTopUsers] = useState([]);
  const [counts, setCounts] = useState({ users: null, teams: null, tiers: null });
  const [usageUser, setUsageUser] = useState(null); // top user to show in the usage modal

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, tu] = await Promise.all([getLLMStats(days), getTopLLMUsers(days, 5)]);
      setStats(s.data);
      setTopUsers(tu.data?.top_users || []);
    } catch { /* surfaced as empty state */ }
    finally { setLoading(false); }
    // counts are range-independent; fetch best-effort and don't block the page
    try {
      const [u, t, p] = await Promise.all([getUsers(1, 1), getTiers(), getProjects()]);
      const teams = Array.isArray(p.data) ? p.data : (p.data?.projects || []);
      setCounts({ users: u.data?.total ?? null, tiers: (t.data?.tiers || []).length, teams: teams.length });
    } catch { /* counts optional */ }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  if (!isAdmin) return <Navigate to="/my-usage" replace />;

  const o = stats?.overall || {};
  const daily = stats?.daily || [];
  const models = (stats?.by_model || []).slice(0, 6);
  const maxModelCost = Math.max(...models.map((m) => m.cost), 0.000001);
  const maxUserCost = Math.max(...topUsers.map((u) => u.cost), 0.000001);

  return (
    <div className="w-full">
      {/* header + range */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-fg flex items-center gap-2"><TrendingUp className="w-6 h-6 text-orange" />Overview</h1>
          <p className="text-fg-muted text-sm mt-1">Platform usage, spend and activity across your deployment.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-panel-border overflow-hidden">
            {RANGES.map((r) => (
              <button key={r.label} onClick={() => setDays(r.days)}
                className={`px-3 py-1.5 text-sm transition-colors ${days === r.days ? 'bg-orange text-white' : 'text-fg-muted hover:text-fg hover:bg-fg/5'}`}>
                {r.label}
              </button>
            ))}
          </div>
          <button onClick={load} title="Refresh" className="p-2 rounded-lg border border-panel-border text-fg-muted hover:text-fg transition-colors"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Stat icon={DollarSign} label="Spend" value={usd(o.total_cost)} accent />
        <Stat icon={Activity} label="Requests" value={compact(o.total_requests)} />
        <Stat icon={ArrowDownToLine} label="Input tokens" value={compact(o.total_input_tokens)} />
        <Stat icon={ArrowUpFromLine} label="Output tokens" value={compact(o.total_output_tokens)} />
      </div>

      {/* counts row */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Link to="/users"><Stat icon={UsersIcon} label="Users" value={counts.users ?? '—'} /></Link>
        <Link to="/projects"><Stat icon={FolderKanban} label="Teams" value={counts.teams ?? '—'} /></Link>
        <Link to="/tiers"><Stat icon={Layers} label="Tiers" value={counts.tiers ?? '—'} /></Link>
      </div>

      {/* charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Panel title="Spend over time">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={daily} margin={{ top: 5, right: 18, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="spendFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f97316" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#f97316" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.12)" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9aa0a6' }} tickLine={false} axisLine={false} tickFormatter={formatDateTick} interval={tickInterval(daily.length)} />
                <YAxis tick={{ fontSize: 11, fill: '#9aa0a6' }} tickLine={false} axisLine={false} width={44} tickFormatter={(v) => '$' + compact(v)} />
                <Tooltip {...tooltipStyle} formatter={(v) => [usd(v), 'spend']} labelFormatter={formatDateTick} />
                <Area type="monotone" dataKey="cost" stroke="#f97316" strokeWidth={2} fill="url(#spendFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Requests over time">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daily} margin={{ top: 5, right: 18, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.12)" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9aa0a6' }} tickLine={false} axisLine={false} tickFormatter={formatDateTick} interval={tickInterval(daily.length)} />
                <YAxis tick={{ fontSize: 11, fill: '#9aa0a6' }} tickLine={false} axisLine={false} width={36} tickFormatter={compact} />
                <Tooltip {...tooltipStyle} formatter={(v) => [compact(v), 'requests']} labelFormatter={formatDateTick} />
                <Bar dataKey="requests" fill="#f97316" radius={[3, 3, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      {/* top models + top users */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Top models by spend" action={<Link to="/llm-stats" className="text-xs text-orange hover:text-orange-hover">Analytics →</Link>}>
          {models.length === 0 ? (
            <p className="text-fg-muted text-sm py-6 text-center">No usage in this range.</p>
          ) : (
            <div className="space-y-3">
              {models.map((m) => (
                <div key={m.model}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-fg font-mono truncate mr-2">{m.model}</span>
                    <span className="text-fg-muted tabular-nums shrink-0">{usd(m.cost)} · {compact(m.requests)} req</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-fg/5 overflow-hidden">
                    <span className="block h-full rounded-full bg-orange" style={{ width: `${Math.max(3, (m.cost / maxModelCost) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Top users by spend" action={<Link to="/llm-stats" className="text-xs text-orange hover:text-orange-hover">Analytics →</Link>}>
          {topUsers.length === 0 ? (
            <p className="text-fg-muted text-sm py-6 text-center">No usage in this range.</p>
          ) : (
            <div className="space-y-3">
              {topUsers.map((u) => (
                <button
                  key={u.user_id}
                  type="button"
                  onClick={() => setUsageUser({ user_id: u.user_id, user_email_id: u.email })}
                  className="block w-full text-left hover:bg-fg/5 -mx-2 px-2 py-1.5 rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-3 text-sm mb-1">
                    <span className="flex-1 min-w-0 text-fg truncate">{u.email}</span>
                    <span className="text-fg-muted tabular-nums shrink-0">{usd(u.cost)} · {compact(u.requests)} req</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-fg/5 overflow-hidden">
                    <span className="block h-full rounded-full bg-orange" style={{ width: `${Math.max(3, (u.cost / maxUserCost) * 100)}%` }} />
                  </div>
                </button>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {usageUser && (
        <UserUsageModal user={usageUser} onClose={() => setUsageUser(null)} />
      )}
    </div>
  );
}
